import './Headline.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useReducer, useRef } from 'preact/hooks';
import { useReducedMotion } from '../../a11y/useReducedMotion';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { EDITORIAL } from '../../copy/editorial';
import { type CopyKey, type Rich, tRich } from '../../copy/strings';
import { fmtVotesLong } from '../../format/numbers';
import { type HeadlineLine, type HeadlineText, headlineAt, headlineText, isClockState } from '../../model/headline';
import type { ResultsView } from '../../model/types';
import { useConfig, useNow, useView } from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';

/** Cópia de saída: animação de 180 ms (A03) e remoção do DOM após 220 ms (§7.4). */
export const HEADLINE_OUT_MS = 220;

/** Marcador do número animado dentro do modelo de texto ("por {V}"): separa o texto em antes / depois. */
const SLOT = '\u0000';

export interface HeadlineProps {
  /** `Countdown` (§10.8), montado dentro do `<h2>` só no estado `countdown`; sem ele, o texto de §13.2.2. */
  countdown?: ComponentChildren;
}

/** Texto rico de §13.2 (nome do candidato em `<b>` na cor do partido, peso 400; §7.4). */
function RichSpans({ rich, view }: { rich: Rich; view: ResultsView | null }) {
  return (
    <>
      {rich.map((part, i) => {
        if (part.candidate === undefined) return part.text;
        const c = view?.candidates.find((x) => x.number === part.candidate);
        const party = c ? partyClass(partyOf(c)) : 'party-fallback';
        return (
          <b key={i} class={`headline__name ${party}`} data-dataviz data-candidate={part.candidate}>
            {part.text}
          </b>
        );
      })}
    </>
  );
}

/** Uma linha do modelo: com `animated`, o número entra por `AnimatedNumber` (§7.4); parado, o texto final. */
function LineContent({ line, view, live }: { line: HeadlineLine; view: ResultsView | null; live: boolean }) {
  // HeadlineCopyKey (model/) é subconjunto de CopyKey: a atribuição tipada pega chave removida do catálogo.
  const key: CopyKey = line.key;
  if (!live || line.animated === undefined) return <RichSpans rich={tRich(key, line.vars)} view={view} />;
  const a = line.animated;
  const rich = tRich(key, { ...line.vars, [a.name]: SLOT });
  return (
    <>
      {rich.map((part, i) => {
        if (part.candidate !== undefined || !part.text.includes(SLOT)) {
          return <RichSpans key={i} rich={[part]} view={view} />;
        }
        const [before, after] = part.text.split(SLOT) as [string, string];
        return (
          <span key={i}>
            {before}
            <AnimatedNumber value={a.value} format={fmtVotesLong} class="headline__num" />
            {after}
          </span>
        );
      })}
    </>
  );
}

/** Estrutura de linhas de §7.4: com duas partes, cada uma é um `span.headline__line` (a 2ª sem quebra). */
function HeadlineBody({ text, view, live }: { text: HeadlineText; view: ResultsView | null; live: boolean }) {
  if (text.line2 === null) return <LineContent line={text.line1} view={view} live={live} />;
  return (
    <>
      <span class="headline__line">
        <LineContent line={text.line1} view={view} live={live} />
      </span>
      <span class="headline__line headline__line--2">
        <LineContent line={text.line2} view={view} live={live} />
      </span>
    </>
  );
}

interface OutCopy {
  text: HeadlineText;
  view: ResultsView | null;
  n: number;
}

/**
 * Manchete do herói (§7.4): estado e texto de §13.2, caixa com 2 linhas reservadas e transição por chave (H7, A03).
 * Quando a chave muda, a manchete anterior vira uma cópia de saída sem `id` e `aria-hidden` (180 ms, removida em 220 ms;
 * nunca mais de uma) e a nova entra com `headline-in` (520 ms). Dentro da mesma chave só o número corre. Com movimento
 * reduzido, troca seca. O relógio da fonte só é assinado nos estados que dependem dele (contagem e abertura, G-10).
 */
export function Headline({ countdown }: HeadlineProps) {
  const view = useView();
  const cfg = useConfig();
  const reduced = useReducedMotion();
  // Sem dado no site ao vivo, o relógio também conta: antes da abertura a manchete diz quando a apuração começa
  // (preOpenHint). No replay, nunca (FB-02, ajuste 1).
  const replay = import.meta.env.VITE_SURFACE === 'replay';
  const clockBound = view !== null ? isClockState(view.headline) : !replay;
  // Assinatura condicional do tique de 1 s (como o StatusIndicator, §6.4): fora da contagem, da abertura e da espera do
  // 1º dado, nenhum render por segundo. `useNow` só lê o sinal (não guarda estado de hook).
  const nowState = clockBound ? useNow() : null;
  const clockTMin = nowState !== null ? nowState.tMin : (view?.instantTMin ?? 0);
  const probabilityOn = cfg.features.probability;
  const state = headlineAt(view, clockTMin, probabilityOn);
  const text = headlineText(
    state,
    { view, clockTMin, probabilityOn, probabilityVisible: view?.probability != null },
    {
      simulation: cfg.mode === 'simulation',
      electionDate: cfg.election_date,
      preOpenHint: view === null && !replay && (nowState?.ms ?? 0) > 0,
      // ORD-F-149 item 1 (em aprovação): o texto da definição matemática pela trava (aprovada a proposta, ORD-F-150)
      decidedText: EDITORIAL[1],
    },
  );

  // Transição (§7.4 bloco 5): detecção da troca de chave no próprio render, para a cópia de saída e a entrada saírem
  // no mesmo quadro da manchete nova.
  const last = useRef<{ text: HeadlineText; view: ResultsView | null } | null>(null);
  const out = useRef<OutCopy | null>(null);
  const enterKey = useRef<string | null>(null);
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const prev = last.current;
  if (prev !== null && prev.text.key !== text.key) {
    if (reduced) {
      out.current = null;
      enterKey.current = null;
    } else {
      out.current = { text: prev.text, view: prev.view, n: (out.current?.n ?? 0) + 1 };
      enterKey.current = text.key;
    }
  }
  last.current = { text, view };
  if (reduced) {
    out.current = null;
    enterKey.current = null;
  }
  const leaving = out.current;
  const outN = leaving?.n ?? 0;
  useEffect(() => {
    if (outN === 0) return;
    const id = setTimeout(() => {
      if (out.current?.n !== outN) return;
      out.current = null;
      rerender(0);
    }, HEADLINE_OUT_MS);
    return () => clearTimeout(id);
  }, [outN]);

  const entering = enterKey.current === text.key;
  const showCountdown = state === 'countdown' && countdown !== undefined && countdown !== null;
  const sub = text.sub ?? null;
  const box = (
    <div class="headline__box">
      {leaving ? (
        <div key={`out-${leaving.n}`} class="headline headline--out" aria-hidden="true" data-testid="headline-out">
          <HeadlineBody text={leaving.text} view={leaving.view} live={false} />
        </div>
      ) : null}
      <h2
        key={text.key}
        id="hero-headline"
        class={entering ? 'headline headline--in' : 'headline'}
        data-testid="headline"
        data-state={state}
        data-key={text.key}
      >
        {showCountdown ? countdown : <HeadlineBody text={text} view={view} live={true} />}
      </h2>
    </div>
  );
  // ORD-F-149 item 1, proposta: a conta da definição matemática, abaixo da manchete (fora do h2)
  if (sub === null || showCountdown) return box;
  return (
    <>
      {box}
      <p class="headline__sub" data-testid="headline-sub">
        <LineContent line={sub} view={view} live={false} />
      </p>
    </>
  );
}
